/**
 * useSiteReliability Hook
 *
 * Fetches the site reliability assessment and keeps it fresh:
 * - polls every 30 s (upstream status refreshes about every 5 min, and the delay
 *   window is 5 min, so faster polling adds little)
 * - pauses while the tab is hidden and refreshes immediately when it becomes visible
 * - never overlaps requests
 * - keeps the last good result when a refresh fails
 */
import { useEffect, useReducer, useRef } from 'react';
import { fetchSiteReliability } from '../api/reliabilityApiClient';
import { useAuth } from '../../auth/useAuth';
import { getAppConfig } from '../../config/appConfig';
import {
  initialReliabilityState,
  reliabilityReducer,
  resultToAction,
  type ReliabilityState,
} from './reliabilityState';

export const RELIABILITY_POLL_INTERVAL_MS = 30_000;

export function useSiteReliability(siteId: string | undefined): ReliabilityState {
  const [state, dispatch] = useReducer(reliabilityReducer, initialReliabilityState);
  const { getAccessToken } = useAuth();

  // A ref so a new getAccessToken identity does not restart polling
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  useEffect(() => {
    if (!siteId) {
      dispatch({ type: 'idle' });
      return;
    }

    dispatch({ type: 'reset' });

    // Mutable bookkeeping is scoped to this effect run and never shared
    let cancelled = false;
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const stopTimer = (): void => {
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
    };

    const scheduleNext = (): void => {
      stopTimer();
      if (!cancelled && !document.hidden) {
        timer = setTimeout(() => void poll(), RELIABILITY_POLL_INTERVAL_MS);
      }
    };

    const poll = async (): Promise<void> => {
      if (inFlight || cancelled) return;
      inFlight = true;
      try {
        const result = await fetchSiteReliability(siteId, {
          apiHost: getAppConfig().apiHost,
          getAccessToken: getAccessTokenRef.current,
        });
        if (!cancelled) dispatch(resultToAction(result));
      } catch (err) {
        if (!cancelled) {
          dispatch({
            type: 'failed',
            message: err instanceof Error ? err.message : 'Failed to load site reliability',
          });
        }
      } finally {
        inFlight = false;
        scheduleNext();
      }
    };

    const onVisibilityChange = (): void => {
      stopTimer();
      if (!document.hidden) void poll();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    void poll();

    return () => {
      cancelled = true;
      stopTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [siteId]);

  return state;
}
